/**
 * RBAC-2.2 · The permission display layer: a label, a one-line description and an area for every
 * row of `public.permissions` that an admin can see (FR-9d, UX-DR4).
 *
 * ⚠️ THIS IS THE ONLY PLACE THE PRODUCT EXPLAINS WHAT AN AUTHORITY DECISION DOES.
 *
 * Every description below was written from what the code measurably does, not from the key's name
 * (RBAC-2.2 call-site audit, 2026-11-01). Where the two disagree the copy follows the code, and
 * `NAME_IS_NOT_REACH` records why. A description that over-claims is a defect: the admin grants a
 * switch for a power it does not carry, which is the Salesforce "View All Data" failure.
 *
 * Rules, all asserted in `permissionDisplay.test.ts`:
 *  - every catalogue row is here exactly once: shown, reader-pending, locked, or hidden with a reason;
 *  - a label starts with what the person can do, carries its own scope, and never contains a key;
 *  - the key is in `permissionDetails()`, for a support conversation, and never in `permissionRow()`.
 *
 * Field and lead copy lives in `fieldPermissions.ts`, commission copy in `commissionPermissions.ts`
 * and `roles.administer` in `rolesAdministration.ts`; this module takes it from there, so each
 * string has one source.
 *
 * Status, from RBAC-0.3's triage (`rbac-0-3-permission-triage-2026-11-01.csv`), corrected by this
 * story's audit in `TRIAGE_CORRECTIONS`:
 *  - `shown`   rendered in the editor;
 *  - `pending` copy written, held back until the KNK / LDG story that reads it lands. A switch that
 *              changes nothing breaks NFR-13, and FR-9a prefers absent to disabled (decided
 *              RBAC-2.2, 2026-11-01);
 *  - `locked`  reserved (FR-12): rendered as a locked row, never as a control;
 *  - hidden    in `HIDDEN_PERMISSIONS`, with the reason. No label: nothing should render it.
 */

import { FIELD_PERMISSIONS } from './fieldPermissions';
import { COMMISSION_PERMISSION_COPY, type CommissionPermission } from './commissionPermissions';
import { ROLES_ADMINISTER } from './rolesAdministration';

export const PERMISSION_AREAS = [
  'Projects',
  'Pipelines',
  'Contracts',
  'Finance & cost',
  'Commission',
  'Leads',
  'Field sales',
  'Reporting',
  'Support tickets',
  'Checklists',
  'Teams',
  'People',
  'Administration',
] as const;

export type PermissionArea = (typeof PERMISSION_AREAS)[number];

export type PermissionRowStatus = 'shown' | 'pending' | 'locked';

/** What the editor renders on a row. Deliberately carries no key. */
export interface PermissionRow {
  label: string;
  description: string;
  area: PermissionArea;
  status: PermissionRowStatus;
}

export interface PermissionDisplay extends PermissionRow {
  key: string;
}

/** What a permission's details panel shows: the row, plus the internal key. */
export interface PermissionDetails {
  key: string;
  label?: string;
  description?: string;
  area?: PermissionArea;
  status: PermissionRowStatus | 'hidden';
  hiddenReason?: string;
}

const LOCKED_SUFFIX = 'Only Joules can grant it.';

type Copy = [key: string, area: PermissionArea, status: PermissionRowStatus, label: string, description: string];

/** Rows whose copy is not owned by `fieldPermissions.ts` or `commissionPermissions.ts`. */
const OWN_COPY: readonly Copy[] = [
  // ── Projects ─────────────────────────────────────────────────────────────
  ['view_projects', 'Projects', 'shown', 'Open redline pages and project tasks',
    "Open the Redlines and Tasks pages, the task menu and a project's Tasks panel, and add notes. It does not decide which projects a person can see."],
  ['edit_projects', 'Projects', 'shown', 'Edit projects',
    "Edit a project's title and overview, add notes, record contributors and commission shares, and set what it was sold under."],
  ['manage_projects', 'Projects', 'shown', 'Manage projects and move them between stages',
    'Everything editing projects allows, plus moving a project between stages directly and managing pipeline stages and finance options.'],
  ['project.manage_all', 'Projects', 'shown', 'Manage projects, including their contracts and reports',
    "Everything managing projects allows, plus a project's Contracts, Lender, Partner, Sales Team and Reports sections. It does not widen which projects a person sees."],
  ['project.manage_team', 'Projects', 'shown', "Edit a project's overview fields",
    "Edit the fields on a project's Overview tab, and nothing else. It is not limited to a team."],
  ['project.view_all', 'Projects', 'shown', 'See redline pricing, including commercial',
    'See the pricing tables on the redline pages and state pricing in the FAQ, and open Commercial Redlines. It does not widen which projects a person sees.'],
  ['project.view_team', 'Projects', 'shown', 'See redline pricing on residential redlines',
    'See the pricing tables on the residential redline pages and state pricing in the FAQ. It is not limited to a team.'],
  ['project.send_email', 'Projects', 'shown', 'Send email from a project',
    'Shows the Emails section on a project, to send and read project email. The server does not check it yet.'],
  // RBAC-6.2 replaced the Design role list (tenant_settings.design_access_role_ids) with this.
  ['design.use', 'Projects', 'shown', 'Use Solar Design',
    "Create and edit solar designs and build proposals from them on a project's Proposals tab. Only in an account that has Solar Design; the server checks it too."],

  // ── Pipelines ────────────────────────────────────────────────────────────
  ['edit_pipeline', 'Pipelines', 'shown', 'Move projects between stages on the residential board',
    'Drag projects from stage to stage on the residential pipeline board. It has no effect on the commercial board.'],
  ['pipeline.manage_all', 'Pipelines', 'shown', 'Manage pipeline stages and board filters',
    "Add and change a pipeline's stages and use the Teams and Project Owner filters on the boards. On the residential board it also allows stage moves."],
  ['view_commercial_pipeline', 'Pipelines', 'shown', 'See the commercial pipelines',
    'Show commercial pipelines in the sidebar and on the dashboard, and open Commercial Redlines. Restrictions set on a pipeline still apply.'],
  ['view_residential_pipeline', 'Pipelines', 'shown', 'See the residential pipelines',
    'Show residential pipelines in the sidebar and on the dashboard. Restrictions set on a pipeline still apply.'],
  ['view_onboarding_pipeline', 'Pipelines', 'shown', 'See the onboarding pipelines',
    'Show the internal onboarding pipelines and open the onboarding pages.'],

  // ── Contracts ────────────────────────────────────────────────────────────
  // RBAC-6.2: the Settings → Contracts role list is retired, so this alone decides the Contracts tab.
  // ⚠️ It does NOT open the Contracts page: that route also needs settings.admin, manage_roles or
  // manage_users (RoleProtectedRoute), so saying so here would over-claim.
  ['view_contracts', 'Contracts', 'shown', 'See contracts',
    "See a project's Contracts tab and Lender section, and who must sign a change order. Only in an account that has Contracts."],
  ['manage_contracts', 'Contracts', 'shown', 'Issue change orders and set who signs them',
    'Everything seeing contracts allows, plus issuing change orders and setting who must sign them.'],

  // ── Finance & cost ───────────────────────────────────────────────────────
  ['view_finance', 'Finance & cost', 'shown', 'See project finance',
    "See a project's Finance section, finance-only fields, commission plans and commission shares."],
  ['edit_finance', 'Finance & cost', 'shown', 'Edit project finance and commission set-up',
    'Edit finance fields, assign commission plans, edit override rules, manager links and trainees, and change contributors and their shares.'],
  ['cost.view', 'Finance & cost', 'shown', 'See job cost and margin',
    'See job cost, cost lines and margin, and the cost and rate card set-up. Without it a person sees no cost figure anywhere.'],
  ['cost.actual.record', 'Finance & cost', 'shown', 'Record actual costs',
    'Record actual costs, shared invoices and overruns against a job. The database checks it; the app does not hide the controls yet.'],
  ['cost.category.manage', 'Finance & cost', 'shown', 'Manage cost categories',
    'Manage the categories every cost is filed under, and link checklist items to them. The database checks it; the app does not hide the controls yet.'],
  ['cost.rate_card.manage', 'Finance & cost', 'shown', 'Manage rate cards',
    "Create and edit rate cards, cost items and installer costs, and the settings that work out a job's cost."],
  ['cost.standard_rate.accept', 'Finance & cost', 'shown', 'Set the standard overhead rate',
    'Propose and confirm the standard overhead rate and choose when overhead is charged to a job. It does not allow editing rate cards.'],

  // ── Commission (the four commission.* rows come from commissionPermissions.ts) ──
  ['comp.agreement.manage', 'Commission', 'shown', 'Manage comp agreement templates',
    'Create, edit and version the comp agreement templates that state how a person is paid.'],

  // ── Reporting (the field reports come from fieldPermissions.ts) ──────────
  ['reporting.admin', 'Reporting', 'shown', 'See reporting and tracking',
    'Open the Reporting and Tracking pages and the Records menu.'],

  // ── Support tickets ──────────────────────────────────────────────────────
  ['manage_tickets', 'Support tickets', 'shown', 'Update any support ticket',
    'Update any support ticket in the account, not only ones they raised or were assigned. The app checks it; the database does not.'],

  // ── Checklists ───────────────────────────────────────────────────────────
  ['manage_checklists', 'Checklists', 'shown', 'Restore deleted checklist items',
    "See and restore deleted items in a checklist's item manager."],

  // ── Teams ────────────────────────────────────────────────────────────────
  ['edit_teams', 'Teams', 'shown', "Change a team's manager",
    'Set or change who manages a team. Creating teams and changing members comes with managing everyone in the account.'],
  ['manage_teams', 'Teams', 'shown', "Set a team's manager",
    "Set or change who manages a team. It does exactly what changing a team's manager does; either one is enough."],

  // ── People ───────────────────────────────────────────────────────────────
  ['manage_users', 'People', 'shown', 'Add people to the account',
    'Add new people to the account, manage every Knock Mode invite, and open shared admin pages. It does not let them edit or remove existing people.'],
  ['user.manage_all', 'People', 'shown', 'Manage everyone in the account',
    'Edit, remove and change the role of anyone in the account, reset passwords, and open the Users, Teams and Installers settings.'],
  ['user.manage_team', 'People', 'shown', 'Add people as a team lead',
    'Add new people to the account. Meant for team leads, but nothing yet limits it to their own team.'],

  // ── Administration ───────────────────────────────────────────────────────
  ['manage_roles', 'Administration', 'shown', 'Open admin pages and delete projects',
    'Open admin pages such as Contracts, the automation builder and video management, and delete projects. It does not control who can edit roles.'],
  ['system.override', 'Administration', 'locked', 'Override every permission',
    `Passes every permission check in the app and the database, and opens the superadmin controls. ${LOCKED_SUFFIX}`],
  ['manage_settings', 'Administration', 'locked', 'Change account settings and administer people',
    `Open Settings, edit, remove and change the role of people, and change commission, finance and account settings. ${LOCKED_SUFFIX}`],
  ['settings.admin', 'Administration', 'locked', 'Use every admin page',
    `Open the admin pages, automation testing, booking links and project deletion. The app checks it; the database does not. ${LOCKED_SUFFIX}`],
  ['tenant.manage', 'Administration', 'locked', 'Manage the account itself',
    `Open the superadmin controls, onboarding pipelines, project deletion and finance layout editing. The app checks it; the database does not. ${LOCKED_SUFFIX}`],
];

/**
 * Permissions created after RBAC-0.3 measured the catalogue, each with the migration that seeds it.
 * The unit test adds these to the CSV's 125; the live suite checks them against `public.permissions`.
 */
export const ADDED_SINCE_RBAC_0_3: Readonly<Record<string, string>> = {
  'roles.administer': '20261101a3_rbac_1_6_seed_roles_administer.sql',
  'design.use': '20261103x2_rbac_6_2_retire_feature_allowlists.sql',
};

/** RBAC-0.3 marked these reader-pending: declared ahead of the KNK / LDG story that reads them. */
const PENDING_FIELD_KEYS: ReadonlySet<string> = new Set([
  'consent.manage', 'dnc.remove', 'field.live_view', 'field.playback', 'field.playback.all',
  'field.playback.team', 'lead.attribution.edit', 'lead.convert', 'lead.delete', 'lead.export',
  'lead.return.manage', 'lead.settings.manage', 'lead.unconvert', 'registration.manage',
  'report.field', 'report.field.all', 'report.field.team', 'report.lead_source',
  'report.statement', 'report.statement.all', 'report.statement.team',
]);

const NO_READER = 'No reader in any repo, policy or function. Granting it changes nothing.';
const BLOG = 'Read only by RLS on the seven blog tables, which hold zero rows and have no screen. An admin cannot observe the effect.';

/**
 * RBAC-2.2's audit found three rows RBAC-0.3 had shown that nothing reads. RBAC-0.3 counted a code
 * comment in `internalAccess.ts` as a reader for the two ticket rows.
 */
export const TRIAGE_CORRECTIONS: Readonly<Record<string, string>> = {
  view_tickets: 'Named only in a comment in internalAccess.ts. Support tickets open without it. Granting it changes nothing.',
  edit_tickets: 'Named only in a comment in internalAccess.ts. Support tickets open without it. Granting it changes nothing.',
  'profitability.view_all': 'Read only by canViewProfitability() in costAuthority.ts, which has no caller outside its test, and by no policy or function. Granting it changes nothing.',
};

/** Rows an admin never sees, each with the reason. Reasons may name keys: they are for us, not for admins. */
export const HIDDEN_PERMISSIONS: Readonly<Record<string, string>> = {
  'commission.export': 'Gates nothing: commission_export_batch() checks commission.pay. Read only by the commission_permission_concentration() report and by copy in commissionPermissions.ts.',
  create_blog_posts: BLOG,
  delete_all_blog_posts: BLOG,
  delete_own_blog_posts: BLOG,
  edit_blog_posts: BLOG,
  manage_blog_categories: BLOG,
  manage_blog_tags: BLOG,
  view_blog_posts: BLOG,
  publish_blog_posts: NO_READER,
  delete_checklists: NO_READER,
  edit_checklists: NO_READER,
  view_checklists: NO_READER,
  delete_commercial_pipeline: NO_READER,
  delete_pipeline: NO_READER,
  edit_commercial_pipeline: 'No live reader: named only by PipelineManageGuard, which nothing renders. Granting it changes nothing.',
  view_pipeline: 'No live reader: named only by PipelineViewGuard, which nothing renders. Granting it changes nothing.',
  delete_projects: 'No live reader: named only by useOptimizedPermissions.canDeleteProjects, which nothing reads. FR-10 assumes it gates deletion; it does not.',
  delete_users: 'No live reader: named only by useOptimizedPermissions.canDeleteUsers, which nothing reads. FR-10 assumes it gates deletion; it does not.',
  edit_users: 'No live reader: named only by useOptimizedPermissions.canEditUsers, which nothing reads. Granting it changes nothing.',
  'user.view_all': 'No live reader: named only by useOptimizedPermissions.canViewAllUsers, which nothing reads. Granting it changes nothing.',
  view_users: NO_READER,
  delete_roles: NO_READER,
  edit_roles: NO_READER,
  view_roles: NO_READER,
  delete_teams: NO_READER,
  view_teams: NO_READER,
  delete_tickets: NO_READER,
  view_settings: NO_READER,
  delete_webhooks: NO_READER,
  edit_webhooks: NO_READER,
  manage_webhooks: NO_READER,
  view_webhooks: NO_READER,
  'team.admin': 'No live reader: named only by TeamAdminGuard, which nothing renders. Granting it changes nothing.',
  'team.member.manage': 'No live reader: named only by TeamMemberManageGuard, which nothing renders. Granting it changes nothing.',
  'team.own': 'No live reader: named only by TeamOwnerGuard, which nothing renders, and by DEFAULT_GRANTS. Granting it changes nothing.',
  'team.pipeline.manage': 'No live reader: named only by two pipeline guards, which nothing renders. Granting it changes nothing.',
  'team.project.manage': NO_READER,
  'team.settings.manage': NO_READER,
  ...TRIAGE_CORRECTIONS,
};

/**
 * Keys whose name promises a scope the code does not apply. Their labels state what they reach
 * instead, and the test holds them to it. RBAC-2.1's mapping must read these as flat strings.
 */
export const NAME_IS_NOT_REACH: Readonly<Record<string, string>> = {
  'project.view_all': 'Every reader ORs it with project.view_team; nothing scopes a project list by it. It shows redline pricing.',
  'project.view_team': 'Every reader ORs it with project.view_all; nothing narrows anything to a team.',
  'project.manage_all': 'Flat: opens project sections, does not decide which projects are reachable.',
  'project.manage_team': "Its one reader is the Overview tab's edit gate; no team test anywhere.",
  'pipeline.manage_all': 'Flat: stage management and board filters, ORed with manage_settings.',
  'user.manage_team': 'create-admin-user accepts it without a team test. Bounded only by which roles the caller may give.',
};

function build(): Record<string, PermissionDisplay> {
  const out: Record<string, PermissionDisplay> = {};
  const add = (row: PermissionDisplay) => {
    if (out[row.key]) throw new Error(`permissionDisplay: ${row.key} declared twice`);
    out[row.key] = row;
  };
  for (const f of FIELD_PERMISSIONS) {
    add({ key: f.key, area: f.area, label: f.label, description: f.description, status: PENDING_FIELD_KEYS.has(f.key) ? 'pending' : 'shown' });
  }
  for (const [key, copy] of Object.entries(COMMISSION_PERMISSION_COPY) as [CommissionPermission, { label: string; explanation: string }][]) {
    if (HIDDEN_PERMISSIONS[key]) continue;
    add({ key, area: 'Commission', label: copy.label, description: copy.explanation, status: 'shown' });
  }
  for (const [key, area, status, label, description] of OWN_COPY) add({ key, area, status, label, description });
  // Shown since RBAC-3.1: canAdministerRoles() gates Settings → Roles & Permissions, and
  // rbac_account_roles() refuses the same callers in the database.
  add({ key: ROLES_ADMINISTER.key, area: ROLES_ADMINISTER.area, label: ROLES_ADMINISTER.label, description: ROLES_ADMINISTER.description, status: 'shown' });
  return out;
}

/** Every visible row, keyed by permission. */
export const PERMISSION_DISPLAY: Readonly<Record<string, PermissionDisplay>> = build();

/** The row the editor renders. No key on it, by design (RBAC-2.2 AC3). */
export function permissionRow(key: string): PermissionRow | null {
  const d = PERMISSION_DISPLAY[key];
  if (!d) return null;
  return { label: d.label, description: d.description, area: d.area, status: d.status };
}

/** The details panel: the row plus the internal key, for a support conversation. */
export function permissionDetails(key: string): PermissionDetails | null {
  const d = PERMISSION_DISPLAY[key];
  if (d) return { ...d };
  const hiddenReason = HIDDEN_PERMISSIONS[key];
  if (hiddenReason) return { key, status: 'hidden', hiddenReason };
  return null;
}

/** Shown and locked rows render; reader-pending and hidden rows wait. */
export function rendersInEditor(key: string): boolean {
  const s = PERMISSION_DISPLAY[key]?.status;
  return s === 'shown' || s === 'locked';
}
